import React from 'react';

import { getAffiliateError, requestAccessFallingBack } from '../api';
import { isOpenableHttpUrl } from '../url-safety';

export interface OpenConsoleResult {
  /** Opens the console; pass a console path (`affiliateConsoleNext`, `affiliateConsoleOverviewNext`) to
   *  deep-link the SSO there. */
  open: (next?: string) => Promise<void>;
}

/**
 * "Advanced Settings": provisions what is missing and opens the single-use SSO url
 * in a new tab (keeping the hub open). SSO is unlimited, so this keeps no in-flight
 * state and never disables its trigger: every click opens its own tab with a fresh
 * single-use link. On failure it throws the normalized affiliate error so the caller
 * can toast it (e.g. AFFILIATES_EMAIL_NOT_VERIFIED / AFFILIATES_NOT_ENABLED).
 *
 * Passing `next` (a console path) deep-links the SSO there, e.g. an affiliate's console page or the
 * Overview on a period; omitting it lands on the brand console root. The deep-link target is
 * BEST-EFFORT: if the backend `/access` refuses the `next` param (a 422, or a 400 on older deploys),
 * we retry without it and still open the brand console root, so the button never regresses; any
 * other failure is reported at once.
 */
export function useOpenConsole(casinoInstanceId: string | undefined): OpenConsoleResult {
  const open = React.useCallback(async (next?: string) => {
    if (!casinoInstanceId) return;
    // Open the tab synchronously inside the click gesture so popup blockers allow it, then
    // point it at the SSO url once the single-use link resolves. `noopener` would null the
    // handle we need, so sever `opener` manually while the tab is still blank/same-origin.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      // A target the backend refuses degrades to the plain brand-root SSO; any other failure throws.
      const access = await requestAccessFallingBack(casinoInstanceId, next);
      // The login_url is minted by the external affiliates.bet API and proxied through; validate
      // the scheme (http/https only) before navigating so a compromised upstream cannot hand us a
      // `javascript:`/`data:` URL to execute in the hub origin (the blank tab / same-tab fallback).
      if (!isOpenableHttpUrl(access.login_url)) throw new Error('No login url returned');
      if (tab && !tab.closed) {
        tab.location.replace(access.login_url);
      } else {
        // Popup blocked / unavailable: fall back to same-tab navigation so the click still works.
        window.location.assign(access.login_url);
      }
    } catch (err: unknown) {
      if (tab && !tab.closed) tab.close();
      throw new Error(getAffiliateError(err).message);
    }
  }, [casinoInstanceId]);

  return { open };
}
